import { type EditorPlugin } from '../../types/aha-editor';
import { type Editor } from 'slate';
import TableUtils from '../TablePlugin/TableUtils';
import { isTableElement } from '../../types/guards';
import AhaEditor from '../../AhaEditor';
import { decodeFragment } from '../../util/fragment';
import { spliceFragmentIntoTable } from '../TablePlugin/util/spliceFragmentIntoTable';

/**
 * This paste handler is primarily optimized to handle pasting multiple cells into a table.
 * If the current selection is not multiple cells, or the incoming fragment only contains a
 * single cell (inside a table), then we fall back to othe paste handlers.
 */
const PasteFragmentIntoTablePlugin: EditorPlugin = {
  name: 'Paste fragment into table plugin',
  insertData: (editor: Editor, data: DataTransfer, controller) => {
    // Don't continue unless there's fragment data
    if (!data.types.includes(`application/${AhaEditor.clipboardFormatKey}`)) {
      return false;
    }

    if (!editor.selection) return false;

    const table = TableUtils.currentTable(editor);
    if (!table) return false;

    const tableSelection = controller?.state.pluginState.tableSelection;
    if (!tableSelection) return false;

    const fragmentString = data.getData(
      `application/${AhaEditor.clipboardFormatKey}`
    );
    const decodedFragment = decodeFragment(fragmentString);

    // If the fragment is not a single table, there's no need to continue
    if (decodedFragment.length !== 1 || !isTableElement(decodedFragment[0])) {
      return false;
    }

    const ahaEditor = AhaEditor.getForEditor(editor);
    const fragment = ahaEditor
      ? ahaEditor.transformPastedFragment(decodedFragment, data)
      : decodedFragment;

    return spliceFragmentIntoTable(editor, tableSelection, fragment);
  },
};

export default PasteFragmentIntoTablePlugin;
